/* import __COLOCATED_TEMPLATE__ from './time-series.hbs'; */
/* RESPONSIBLE TEAM: team-ai-insights */
import Component from '@glimmer/component';
import { staticDataResource } from 'embercom/lib/reporting/static-data-resource';
import { use } from 'ember-resources/util/function-resource';
import type { Chart, ChartEventsOptions, Options, Point, SeriesArearangeOptions } from 'highcharts';
import {
  flattenSegmentBy,
  type MetricValue,
  type Series,
} from 'embercom/lib/reporting/chart-data-resource';
import { getNumericValue } from 'embercom/lib/reporting/chart-data-resource-compatible';
import type { ReportState } from 'embercom/components/reporting/custom/report/chart-card';
import { cached } from 'tracked-toolbox';
import Highcharts from 'highcharts';
import {
  addTopicFilter,
  colorForNoMetricValue,
  layoutColorAxisLabels,
  metricConfigs,
  topicPrefix,
} from './common';
import moment, { type Moment } from 'moment-timezone';
import type Range from 'embercom/models/reporting/range';
import { closestSafeIntervalFor } from 'embercom/lib/reporting/custom/safe-limits';
import { inject as service } from '@ember/service';
import type ThemeService from 'embercom/services/theme-service';
import PALETTE from '@intercom/pulse/lib/palette';
import type IntlService from 'ember-intl/services/intl';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';

interface Args {
  reportState: ReportState;
  limit: number;
  metricId: string;
  topicId: string | null;
  hoveredTopicId?: string | null;
  onTopicHovered?: (topicId: string | null) => void;
  onShowConversationExplorer: (
    topic: string,
    subtopic: string | null,
    dateRange: Range,
    conversationCount: number,
  ) => void;
  trackAnalyticsEvent: (event: string, context: string, data?: Record<string, any>) => void;
}

interface TimeSeriesPoint extends Point {
  metricValue: MetricValue;
  volume: number;
  colorAxisValue: number;
}

function sumAllValues(series: Series) {
  return series
    .map(([_key, metricValue]) => getNumericValue(metricValue))
    .compact()
    .reduce((a, b) => a + b, 0);
}

function getAllValues(series: Series) {
  return series.map(([_timestamp, metricValue]) => getNumericValue(metricValue)).compact();
}

// given the palette and a value between 0 and 1, return the color at that point in the palette
// and interpolate between colors if the value is not exactly at a break point in the palette
function interpolateColor(palette: Highcharts.ColorType[], value: number) {
  let paletteValue = value * (palette.length - 1);
  let paletteIndex1 = Math.floor(paletteValue);
  let paletteIndex2 = Math.ceil(paletteValue);
  let interpolationDistance = paletteValue - paletteIndex1;

  let color1 = Highcharts.color(palette[paletteIndex1]);
  let color2 = Highcharts.color(palette[paletteIndex2]);

  return color1.tweenTo(color2, interpolationDistance);
}

function getColorForValue(
  palette: Highcharts.ColorType[],
  value: number,
  minValue: number,
  maxValue: number,
) {
  let range = maxValue - minValue;
  let normalizedValue = range === 0 ? 0.5 : (value - minValue) / range;
  return interpolateColor(palette, normalizedValue);
}

// the existing Range class quantized the range, e.g. endMoment uses .endOf('day')
// so it won't work with small intervals and has strange behaviour for ranges of a single day
// this class is a simpler version that looks the same, but just uses the raw numbers
// I've confirmed that the query on the backend uses gte start and lt end.
class SimpleDateTimeRange {
  startMoment: Moment;
  endMoment: Moment;
  timezone: string;

  constructor(start: number, end: number, timezone: string) {
    this.startMoment = moment.tz(start, timezone);
    this.endMoment = moment.tz(end, timezone);
    this.timezone = timezone;
  }
}

const maxOverlap = 1.0;
const RIDGELINE_HEIGHT = 76;
const LEGEND_AREA_HEIGHT = 84;

export default class TimeSeries extends Component<Args> {
  @service declare themeService: ThemeService;
  @service declare intl: IntlService;

  @tracked tooltipOpenerElement: HTMLElement | undefined = undefined;
  @tracked hoveredPoint: TimeSeriesPoint | null = null;
  @tracked hideTooltip = false;
  tooltipTimeout: ReturnType<typeof setTimeout> | undefined = undefined;
  readonly tooltipPositionerClass = 'time-series-tooltip-positioner';
  @tracked resetZoom = () => {}; // this gets overridden by the afterShowResetZoom event
  @tracked chart: Chart | null = null;

  @use topicsData = staticDataResource(this, {
    serializedChart: () => this.chartDefinition,
    reportState: () => this.args.reportState,
  });

  labelSegmenter = new Intl.Segmenter();

  @cached
  get metricConfig() {
    return metricConfigs(this.intl)[this.args.metricId];
  }

  get timeBucketInterval() {
    if (this.timeInterval === 'hour') {
      return 1000 * 60 * 60;
    } else if (this.timeInterval === 'day') {
      return 1000 * 60 * 60 * 24;
    }
    return 0;
  }

  get timeInterval() {
    let days = this.args.reportState.dateRange.inDays;
    if (days === 1) {
      return 'hour';
    }
    return closestSafeIntervalFor('day', days);
  }

  get timestampOffsetForCurrentInterval() {
    if (this.timeInterval === 'hour') {
      return 1000 * 60 * 30;
    } else if (this.timeInterval === 'day') {
      return 1000 * 60 * 60 * 12;
    } else if (this.timeInterval === 'week') {
      return 1000 * 60 * 60 * 24 * 7;
    }
    return 0;
  }

  get chartDefinition() {
    if (this.args.topicId === null) {
      return this.topicsChartDefinition;
    }
    return this.subtopicChartDefinition;
  }

  get topicsChartDefinition() {
    return {
      view_by: 'time',
      view_by_time_interval: this.timeInterval,
      segment_by: 'conversation_custom_fields#ai_insights_topic',
      segment_by_display_limit: 500,
      chart_series: [{ metric_id: 'v1.new_conversations' }, this.metricConfig.seriesConfig],
    };
  }

  get subtopicChartDefinition() {
    if (this.args.topicId === null) {
      throw new Error('topicId is required for subtopic chart definition');
    }

    return {
      view_by: 'time',
      view_by_time_interval: this.timeInterval,
      segment_by: 'conversation_custom_fields#ai_insights_topic_subtopic',
      segment_by_display_limit: 500,
      chart_series: [
        addTopicFilter({ metric_id: 'v1.new_conversations' }, this.args.topicId),
        addTopicFilter(this.metricConfig.seriesConfig, this.args.topicId),
      ],
    };
  }

  get tooltipTitle() {
    if (this.hoveredPoint) {
      // eslint-disable-next-line @intercom/intercom/no-bare-strings
      let dateTimeFormat = this.timeInterval === 'hour' ? 'MMM D HH:mm' : 'MMMM D';
      return moment
        .tz(this.hoveredPoint.x, this.args.reportState.dateRange.timezone)
        .format(dateTimeFormat);
    }
    return '';
  }

  get tooltipMetricValues() {
    if (!this.hoveredPoint) {
      return [];
    }

    let { metricValue, volume } = this.hoveredPoint;
    let formattedMetricValue = this.topicsData.formatValue({
      value: metricValue,
      metricId: this.args.metricId,
      aggregation: this.metricConfig.seriesConfig?.aggregation,
      durationFormat: 'hours',
    });

    return [
      {
        label: this.intl.t('components.reporting.custom.report.conversation-stream.conversations'),
        value: volume,
      },
      {
        label: this.metricConfig.label,
        value: formattedMetricValue,
      },
    ];
  }

  get highchartsOptions(): Options & { tooltip?: { crosshairs?: boolean } } {
    if (!this.highchartsSeries) {
      return {};
    }
    let stops: Array<[number, string]> = this.metricConfig.colorAxis.stops.map(
      ([index, paletteColor]) => [index, PALETTE[paletteColor]],
    );

    // noop to force recalculation when the theme changes
    this.themeService.darkModeEnabled;

    let numberOfSeries = this.segmentedVolumeData.size;
    let minPlotAreaHeight = numberOfSeries * RIDGELINE_HEIGHT;
    let containerHeight =
      (document.querySelector('.time-series-chart') as HTMLElement).offsetHeight - 20;

    let component = this;
    return {
      chart: {
        animation: false,
        marginBottom: 80,
        zooming: {
          type: 'x',
          mouseWheel: true,
        },
        panning: {
          type: 'x',
          enabled: true,
        },
        panKey: 'shift',
        // We explicitly set this as the leaving it as transparent was causing
        // the scrollable plot area mask to not be visible
        backgroundColor: 'var(--base-module)',
        scrollablePlotArea: {
          // Use a dynamic minHeight to keep the ridgelines at a stable height
          minHeight: minPlotAreaHeight,
          scrollPositionY: 0,
          opacity: 1.0,
        },
        // Use a dynamic height to keep the ridgelines at a stable height when
        // there are only a few series
        height:
          minPlotAreaHeight + LEGEND_AREA_HEIGHT > containerHeight || numberOfSeries === 0
            ? null
            : minPlotAreaHeight + LEGEND_AREA_HEIGHT - numberOfSeries * 4,
        events: {
          afterShowResetZoom: (args: any) => {
            component.showResetZoomButton(true);
            let chart = args.target as Chart;
            component.resetZoom = () => {
              chart.zoomOut();
              component.args.trackAnalyticsEvent('zoomed-out', 'time-series');
            };
            return true;
          },
          selection: (args: any) => {
            if (args.resetSelection === true) {
              component.showResetZoomButton(false);
            } else {
              component.args.trackAnalyticsEvent('zoomed-in', 'time-series');
            }
            return true;
          },
          destroy: (_args: any) => {
            // this covers the edge case of being zoomed in and the number of series changes
            // so the reset zoom button should hidden
            component.showResetZoomButton(false);
            return true;
          },
          render: (args: any) => {
            let chart = args.target as Chart;
            layoutColorAxisLabels(chart);
            // Hide the topmost gridline and tick
            Object.entries(chart.yAxis[0].ticks).forEach(([_, tick]) => {
              if (tick.isLast) {
                tick.gridLine?.hide();
                tick.mark?.hide();
              }
            });
            component.chart = chart;
            return true;
          },
        } as ChartEventsOptions,
      },
      time: {
        timezone: component.args.reportState.dateRange.timezone,
      },
      legend: {
        enabled: true,
        symbolRadius: 6, // makes the colorAxis have rounded corners
        align: 'left',
        x: 0,
        y: 25,
      },
      colorAxis: {
        min: this.extremes.minMetricValue,
        max: this.extremes.maxMetricValue,
        className: 'font-mono font-medium',
        gridLineColor: 'transparent',
        showFirstLabel: true,
        showLastLabel: true,
        tickPositions: [
          this.extremes.minMetricValue,
          this.extremes.minMetricValue + 1,
          this.extremes.maxMetricValue,
        ],
        stops,
        labels: {
          useHTML: false,
          align: 'left',
          step: 2,
          style: {
            fontSize: '12px',
            color: 'var(--text-default)',
          },
          formatter(this: Highcharts.AxisLabelsFormatterContextObject) {
            return component.topicsData.formatValue({
              value: this.value as any,
              metricId: component.args.metricId,
              aggregation: component.metricConfig.seriesConfig?.aggregation,
              durationFormat: 'hours',
            });
          },
        },
      },
      tooltip: { enabled: false },
      plotOptions: {
        series: {
          stickyTracking: false, // this triggers hovering only over actual points, not the area
          animation: false,
          marker: {
            enabled: false,
          },
          states: {
            hover: {
              enabled: false,
              animation: false,
            },
            normal: {
              animation: false,
            },
            inactive: {
              opacity: 0.3,
              animation: false,
            },
          },
          lineWidth: 1,
          cursor: 'pointer',
          events: {
            mouseOut() {
              component.args.onTopicHovered?.(null);
              component.tooltipOpenerElement = undefined;
              if (component.tooltipTimeout) {
                clearTimeout(component.tooltipTimeout);
                component.tooltipTimeout = undefined;
              }
            },
          },
          point: {
            events: {
              click(this: TimeSeriesPoint) {
                component.onViewConversations();
              },
              mouseOver(this: TimeSeriesPoint) {
                component.hoveredPoint = this;
                let chart = this.series.chart;
                // accessing the scrollablePlotArea like this is a workaround to get the scroll position
                let scrollYOffset: number =
                  (chart as any).scrollablePlotArea?.scrollingContainer?.scrollTop ?? 0;
                let x = chart.plotLeft + (this.plotX || 0);
                let y = chart.plotTop + (this.plotY || 0) - scrollYOffset;

                // move the tooltip positioner to the hovered point, then
                // we open the Popover on the poistioner element
                let positioner = document.querySelector(
                  `.${component.tooltipPositionerClass}`,
                ) as HTMLElement;
                if (positioner) {
                  positioner.style.left = `${x}px`;
                  positioner.style.top = `${y}px`;
                }

                // Clear any existing timeout
                if (component.tooltipTimeout) {
                  clearTimeout(component.tooltipTimeout);
                }

                // Always reset tooltip state and show with delay
                component.hideTooltip = true;
                component.tooltipOpenerElement = undefined;
                component.tooltipTimeout = setTimeout(() => {
                  component.hideTooltip = false;
                  component.tooltipOpenerElement = positioner;
                }, 10);

                // Emit hover event to parent for cross-component highlighting
                let seriesId = this.series.userOptions.id!;
                component.args.onTopicHovered?.(seriesId);
                return true;
              },
              mouseOut(this: TimeSeriesPoint) {
                component.tooltipOpenerElement = undefined;
                return true;
              },
            },
          },
        },
      },
      xAxis: {
        type: 'datetime',
        plotBands: this.buildPlotBandsForWeekends(),
        crosshair: {
          dashStyle: 'ShortDash',
          zIndex: 50,
        },
      },
      yAxis: {
        title: { text: null },
        gridLineColor: 'var(--neutral-border)',
        categories: this.highchartsSeries.map((series) => series.name ?? ''),
        min: 0,
        max: this.highchartsSeries.length - 1,
        tickLength: 150,
        tickWidth: 1,
        tickColor: 'var(--neutral-border)',
        labels: {
          useHTML: true,
          formatter(this: Highcharts.AxisLabelsFormatterContextObject) {
            // This allows us to show multi-line labels (max 3 lines)
            // that are aligned to the bottom and truncate with an ellipsis
            // if the text can't fit on three lines.
            return `
            <div class="h-14 w-32 flex items-end">
              <div class="max-h-14 w-32 line-clamp-3 text-ellipsis overflow-hidden" style="text-wrap-mode: wrap">${this.value}</div>
            </div>`;
          },
          align: 'left',
          reserveSpace: true,
          x: -20,
          y: -10, // offset half a line height, plus a bit more to clear the tick mark
          style: {
            width: 130,
            fontSize: '12px',
            color: 'var(--text-default)',
            textOverflow: 'lineClamp',
          },
        },
        startOnTick: true,
        endOnTick: true,
        gridLineWidth: 1,
        tickmarkPlacement: 'between',
      },
    };
  }

  showResetZoomButton(show: boolean) {
    let el = document.querySelector('.time-series-reset-zoom-button') as HTMLElement;
    if (el) {
      el.style.visibility = show ? 'visible' : 'hidden';
    }
  }

  truncateLabel(label: string, maxLength: number) {
    let labelSegments = Array.from(this.labelSegmenter.segment(label));
    if (labelSegments.length > maxLength) {
      let truncatedLabel = labelSegments
        .slice(0, maxLength)
        .map(({ segment }) => segment)
        .join('');
      return `${truncatedLabel}...`;
    }
    return label;
  }

  @cached
  get segmentedVolumeData() {
    let allSeries = flattenSegmentBy(this.topicsData.rawChartData[0]);

    if (this.args.topicId) {
      // Filter to only the subtopics that start with the chosen topic
      let prefix = topicPrefix(this.args.topicId);
      allSeries = allSeries.filter(([segmentByKey, _series]) => {
        return (segmentByKey as string).startsWith(prefix);
      });
    }

    // TODO: the backend is possibly sorting and limiting for us, but we should confirm
    allSeries.sort((a, b) => {
      let [_seriesKey, series] = a;
      let [_otherSeriesKey, otherSeries] = b;
      return sumAllValues(otherSeries) - sumAllValues(series);
    });

    let limited = allSeries.slice(0, this.args.limit);
    return new Map(limited);
  }

  @cached
  get segmentedMetricData() {
    let allSeries = flattenSegmentBy(this.topicsData.rawChartData[1]);
    let topSeries = allSeries.filter(([seriesKey, _series]) => {
      return this.segmentedVolumeData.has(seriesKey);
    });

    return new Map(topSeries);
  }

  @cached
  get extremes() {
    let volumeValuesFromAllSeries = Array.from(this.segmentedVolumeData.values())
      .map(getAllValues)
      .flat()
      .compact();
    let metricValuesFromAllSeries = Array.from(this.segmentedMetricData.values())
      .map(getAllValues)
      .flat()
      .compact();
    let volumeKeysFromAllSeries = Array.from(this.segmentedVolumeData.values())
      .map((series) => series.map(([key, _value]) => key as number))
      .flat()
      .compact();

    return {
      minVolumeValue: Math.min(...volumeValuesFromAllSeries),
      maxVolumeValue: Math.max(...volumeValuesFromAllSeries),
      minMetricValue: Math.min(...metricValuesFromAllSeries),
      maxMetricValue: Math.max(...metricValuesFromAllSeries),
      minKey: Math.min(...volumeKeysFromAllSeries),
      maxKey: Math.max(...volumeKeysFromAllSeries),
    };
  }

  buildSeries(seriesId: string, seriesIndex: number): SeriesArearangeOptions {
    // -0.5 moves down half the height of the ridgeline, so the top ridge line
    // doesn't get cut off at the top of the chart
    let seriesYOffset = seriesIndex - 0.5;
    let volumeDataSeries = this.segmentedVolumeData.get(seriesId) || [];
    // convert nulls to zeros for display in area charts so data points
    // with adjacent nulls are connected and rendered correctly
    volumeDataSeries = volumeDataSeries.map(([timestamp, metricValue]) => [
      timestamp,
      metricValue || 0,
    ]);
    let metricDataSeries = new Map(this.segmentedMetricData.get(seriesId) || []);

    let [topicName, subtopicName] = seriesId.split('=>');
    let palette = this.metricConfig.colorAxis.stops.map(([_, color]) => PALETTE[color]);

    return {
      type: 'arearange',
      id: seriesId,
      name: subtopicName ?? topicName,
      zIndex: this.args.limit - seriesIndex + 1,
      colorKey: 'colorAxisValue',
      color: 'transparent', // line colour on top of the area
      data: volumeDataSeries.map(([timestamp, metricValue]: [number, MetricValue]) => {
        let volume = getNumericValue(metricValue);
        if (volume === null) {
          return {
            x: timestamp,
            low: undefined,
            high: undefined,
          };
        }
        let normalizedVolume = maxOverlap * (volume / this.extremes.maxVolumeValue);
        let secondMetricValue = getNumericValue(metricDataSeries.get(timestamp)!);
        return {
          x: timestamp,
          low: seriesYOffset,
          high: seriesYOffset + normalizedVolume,
          volume,
          metricValue: metricDataSeries.get(timestamp),
          colorAxisValue: secondMetricValue,
        } as TimeSeriesPoint;
      }),
      zoneAxis: 'x',
      zones: Array.from(metricDataSeries.entries())
        .map(([timestamp, metricValue]: [number, MetricValue]) => {
          let numericValue = getNumericValue(metricValue);
          if (numericValue === null || timestamp === null) {
            return {
              value: timestamp + this.timeBucketInterval / 2,
              color: colorForNoMetricValue,
            };
          }
          return {
            value: timestamp + this.timeBucketInterval / 2,
            fillColor: getColorForValue(
              palette,
              numericValue,
              this.extremes.minMetricValue,
              this.extremes.maxMetricValue,
            ),
          };
        })
        .compact(),
    };
  }

  buildPlotBandsForWeekends() {
    let plotBands = [] as Highcharts.XAxisPlotBandsOptions[];

    let currentTime = moment
      .tz(this.extremes.minKey, this.args.reportState.dateRange.timezone)
      .startOf('day');
    let endTime = moment.tz(this.extremes.maxKey, this.args.reportState.dateRange.timezone);

    while (currentTime <= endTime) {
      if (currentTime.day() === 6) {
        plotBands.push({
          from: currentTime.valueOf(),
          to: currentTime.add(2, 'days').valueOf(),
          color: 'var(--neutral-container)',
        });
      }
      currentTime.add(1, 'day');
    }
    return plotBands;
  }

  @cached
  get highchartsSeries(): SeriesArearangeOptions[] {
    return Array.from(this.segmentedVolumeData.keys())
      .reverse()
      .map((seriesName: string, seriesIndex) => this.buildSeries(seriesName, seriesIndex));
  }

  // Reactive effect to update highlighting when hoveredTopicId changes
  get triggerHighlightingUpdate() {
    let hoveredTopicId = this.args.hoveredTopicId;

    this.updateSeriesHighlightingWithStates(hoveredTopicId || null);
    return true;
  }

  @action
  onViewConversations() {
    if (this.hoveredPoint) {
      let rangeStart = this.hoveredPoint.x;
      let rangeEnd = this.hoveredPoint.x + this.timeBucketInterval;
      let timeBucketRange = new SimpleDateTimeRange(
        rangeStart as any,
        rangeEnd as any,
        this.args.reportState.dateRange.timezone,
      );
      let [topicId, subtopicId] = this.hoveredPoint.series.userOptions.id!.split('=>');
      this.args.onShowConversationExplorer(
        topicId,
        subtopicId ?? null,
        timeBucketRange as any,
        this.hoveredPoint.volume,
      );
    }
  }

  @action
  updateSeriesHighlightingWithStates(externalHoveredId: string | null) {
    if (!this.chart || !this.chart.series) {
      return;
    }
    this.chart.series.forEach((series) => {
      if (!series.data) {
        return;
      }
      if (!series.options || !series.options.id) {
        return;
      }
      if (externalHoveredId) {
        series.setState(externalHoveredId === series.options.id ? 'normal' : 'inactive');
      } else {
        series.setState('normal');
      }
    });
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Reporting::Automation::FinAiAgent::Analyze::TopicsExplorer::TimeSeries': typeof TimeSeries;
  }
}
